export class BlockUI {
  private targetElement: HTMLElement;
  private overlayElement: HTMLElement | null = null;
  private isBlocking = false;

  constructor(target?: HTMLElement | string) {
    if (typeof target === "string") {
      const element = document.querySelector(target) as HTMLElement;
      if (!element) {
        throw new Error(`Element with selector "${target}" not found`);
      }
      this.targetElement = element;
    } else if (target instanceof HTMLElement) {
      this.targetElement = target;
    } else {
      // Default to body for full screen blocking
      this.targetElement = document.body;
    }
  }

  public block(options?: string | { message?: string; spinner?: boolean }): void {
    if (this.isBlocking) return;

    this.isBlocking = true;
    const message = typeof options === "string" ? options : options?.message;
    const spinner = typeof options === "object" ? (options.spinner ?? false) : false;
    this.createOverlay(message, spinner);
    this.attachOverlay();
  }

  public unblock(): void {
    if (!this.isBlocking || !this.overlayElement) return;

    this.isBlocking = false;
    this.removeOverlay();
  }

  public isBlocked(): boolean {
    return this.isBlocking;
  }

  private createOverlay(message?: string, spinner = false): void {
    this.overlayElement = document.createElement("div");

    // Base overlay styles
    this.overlayElement.className =
      "d-flex justify-content-center align-items-center";
    this.overlayElement.style.cssText = `
      position: ${this.targetElement === document.body ? "fixed" : "absolute"};
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.3);
      z-index: 9999;
      backdrop-filter: blur(1px);
    `;

    // Create spinner container
    const spinnerContainer = document.createElement("div");
    spinnerContainer.className = "text-center p-4";

    if (spinner) {
      const spinnerEl = document.createElement("div");
      spinnerEl.className = "spinner-border text-white mb-3";
      spinnerEl.setAttribute("role", "status");
      spinnerEl.style.width = "3rem";
      spinnerEl.style.height = "3rem";

      const spinnerSr = document.createElement("span");
      spinnerSr.className = "visually-hidden";
      spinnerSr.textContent = "Loading...";
      spinnerEl.appendChild(spinnerSr);
      spinnerContainer.appendChild(spinnerEl);
    }

    if (message) {
      const messageElement = document.createElement("div");
      messageElement.className = "text-white";
      messageElement.textContent = message;
      spinnerContainer.appendChild(messageElement);
    }

    this.overlayElement.appendChild(spinnerContainer);
  }

  private attachOverlay(): void {
    if (!this.overlayElement) return;

    // Set target element position to relative if it's not already positioned
    if (this.targetElement !== document.body) {
      const computedStyle = window.getComputedStyle(this.targetElement);
      if (computedStyle.position === "static") {
        this.targetElement.style.position = "relative";
      }
    }

    this.targetElement.appendChild(this.overlayElement);
  }

  private removeOverlay(): void {
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
      this.overlayElement = null;
    }
  }
}
